import type {
  HapticStyleType,
  ShellEventType,
  ShellMessageType,
} from '@featit/shared/shell-bridge';
import { SHELL_MESSAGE_HANDLER_NAME } from '@featit/shared/shell-paths';
import {
  hasShellCapability,
  installShellDispatch,
  isShellRuntime,
  shellDescriptor,
} from './shellDetection';

/**
 * The web's half of the bridge (T-0320): post a message to the shell, ask it something and wait for
 * the answer, and hear the events it dispatches.
 *
 * A leaf apart from the detection it sits on, so the session vault can use it from under
 * `ApiService` without closing a cycle.
 */

type ShellEventListener = (event: ShellEventType) => void;

const listeners = new Set<ShellEventListener>();
const pending = new Map<
  string,
  { resolve: (value: unknown) => void; reject: (error: Error) => void }
>();
let requestSequence = 0;
let installed = false;

/**
 * I-2453 S3 (SBS-8): the event check (`@featit/shared/shell-event-check`) is a chunk of its own,
 * loaded with the shell's runtime (`loadShellRuntime`); only the app shell dispatches events, so a
 * browser never loads it. An event that arrives before it is in waits here, in order, and is checked
 * and delivered as soon as it is: none is dropped for arriving early, and none acted on unchecked.
 */
type ShellEventCheckType = (value: unknown) => value is ShellEventType;
let eventCheck: ShellEventCheckType | null = null;
let loadingEventCheck: Promise<void> | null = null;
const earlyEvents: unknown[] = [];
/** More than a launch's worth of events (insets, keyboard, connectivity, scope); beyond, dropped. */
const EARLY_EVENTS_MAX = 200;
/**
 * I-2561 (I-1380): until the boot has sent its session check (`allowShellEventCheck`), an early event
 * only waits, so fetching the check's chunks never goes ahead of that request.
 */
let eventCheckAllowed = false;

/**
 * I-2561 (I-1380): a reply is delivered without the event check's chunk. The Keychain's answer to the
 * boot's session read is one, and the session check cannot be sent before it; waiting for the chunk
 * put its three files ahead of that request. The rule is the chunk's own for a reply
 * (`isShellEvent`, case 'reply'), and each requester checks its own value.
 */
function isShellReply(value: unknown): value is Extract<ShellEventType, { type: 'reply' }> {
  if (typeof value !== 'object' || value === null) return false;
  const event = value as Record<string, unknown>;
  return (
    event.type === 'reply' && typeof event.requestId === 'string' && typeof event.ok === 'boolean'
  );
}

/**
 * The boot's session check is out (`index.tsx`, `bootThenRender`'s `startSession`): from now on an
 * early event loads the check, and what already waits is checked and delivered once it is in.
 */
export function allowShellEventCheck(): void {
  if (eventCheckAllowed) return;
  eventCheckAllowed = true;
  if (!eventCheck && earlyEvents.length > 0) {
    loadShellEventCheck().catch((error: unknown) =>
      console.warn('[featit] shell bridge: the event check did not load', error),
    );
  }
}

/** Load the event check, once; then deliver what waited for it. */
export function loadShellEventCheck(): Promise<void> {
  loadingEventCheck ??= import('@featit/shared/shell-event-check').then(
    (module) => {
      eventCheck = module.isShellEvent;
      for (const event of earlyEvents.splice(0)) checkAndDeliver(event);
    },
    (error: unknown) => {
      // A failed chunk is asked for again by the next event rather than remembered.
      loadingEventCheck = null;
      throw error;
    },
  );
  return loadingEventCheck;
}

/**
 * How long a request waits for its `reply`. A Keychain that is asking for Face ID can take a while;
 * a shell that never answers must not hang the app, and every caller has a fallback.
 */
export const SHELL_REQUEST_TIMEOUT_MS = 10_000;

/** Send a message to the shell. `false` in a browser, or when the handler is missing. */
export function postShellMessage(message: ShellMessageType): boolean {
  if (!isShellRuntime()) return false;
  const handler = window.webkit?.messageHandlers?.[SHELL_MESSAGE_HANDLER_NAME];
  if (!handler) return false;
  try {
    handler.postMessage(message);
    return true;
  } catch (error) {
    console.warn('[featit] shell bridge: posting failed', message.type, error);
    return false;
  }
}

type RequestMessageType = Extract<ShellMessageType, { requestId: string }>;
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
/** A request as the caller writes it; the bridge adds the `requestId`. */
export type ShellRequestType = DistributiveOmit<RequestMessageType, 'requestId'>;

export interface IShellRequestOptions {
  /**
   * How long to wait for the reply; `SHELL_REQUEST_TIMEOUT_MS` when left out. `null` waits for as
   * long as the answer takes, and only for a request a person answers on a native prompt with
   * nothing on screen waiting on it (`pushToken`, I-1070): cut off, the answer that arrives later
   * is thrown away.
   */
  timeoutMs?: number | null;
}

/**
 * Ask the shell something and wait for its `reply`. Rejects in a browser, on a refusal from the
 * shell, and after `SHELL_REQUEST_TIMEOUT_MS` (or `options.timeoutMs`). `TValue` is the request's
 * entry in `IShellReplyValues`; the shell's answer is data from outside, so callers still check its
 * shape.
 */
export function requestShell<TValue>(
  message: ShellRequestType,
  options: IShellRequestOptions = {},
): Promise<TValue> {
  installShellBridge();
  requestSequence += 1;
  const requestId = `web-${requestSequence}-${Date.now().toString(36)}`;
  const timeoutMs = options.timeoutMs === undefined ? SHELL_REQUEST_TIMEOUT_MS : options.timeoutMs;
  return new Promise((resolve, reject) => {
    const timer =
      timeoutMs === null
        ? undefined
        : setTimeout(
            () => pending.get(requestId)?.reject(new Error('the app shell did not answer')),
            timeoutMs,
          );
    const settle = () => {
      pending.delete(requestId);
      clearTimeout(timer);
    };
    pending.set(requestId, {
      resolve: (value) => {
        settle();
        resolve(value as never);
      },
      reject: (error) => {
        settle();
        reject(error);
      },
    });
    if (!postShellMessage({ ...message, requestId } as ShellMessageType)) {
      pending.get(requestId)?.reject(new Error('not in the app shell'));
    }
  });
}

/**
 * Ask the shell for one haptic (*Haptics, all in one place*). A no-op without the `haptics`
 * capability — a browser, mobile Safari, or an app built before IU-11's native generator — so a
 * caller never has to check the capability itself before asking.
 */
export function sendHaptic(style: HapticStyleType): void {
  if (!hasShellCapability('haptics')) return;
  postShellMessage({ type: 'haptic', style });
}

/** Hear every event the shell dispatches, except replies. Returns the unsubscribe. */
export function onShellEvent(listener: ShellEventListener): () => void {
  installShellBridge();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * What the shell calls, as `window.featitShell.dispatch(event)`. A malformed or unknown event is
 * dropped whole, never half-applied.
 */
export function dispatchShellEvent(event: unknown): void {
  if (!eventCheck) {
    if (isShellReply(event)) {
      deliver(event);
      return;
    }
    if (earlyEvents.length < EARLY_EVENTS_MAX) earlyEvents.push(event);
    else console.warn('[featit] shell bridge: dropped an event that came before its check loaded');
    if (!eventCheckAllowed) return;
    loadShellEventCheck().catch((error: unknown) =>
      console.warn('[featit] shell bridge: the event check did not load', error),
    );
    return;
  }
  checkAndDeliver(event);
}

function checkAndDeliver(event: unknown): void {
  const check = eventCheck;
  if (!check || !check(event)) {
    console.warn('[featit] shell bridge: dropped an event it does not understand', event);
    return;
  }
  deliver(event);
}

function deliver(event: ShellEventType): void {
  if (event.type === 'reply') {
    const waiting = pending.get(event.requestId);
    if (!waiting) return;
    if (event.ok) waiting.resolve(event.value);
    else waiting.reject(new Error(event.error));
    return;
  }
  for (const listener of [...listeners]) {
    try {
      listener(event);
    } catch (error) {
      console.error('[featit] shell bridge: a listener failed', event.type, error);
    }
  }
}

/** Install the dispatch function on the descriptor, once. Idempotent; nothing in a browser. */
export function installShellBridge(): void {
  if (installed) return;
  installed = installShellDispatch(dispatchShellEvent);
  // For the stylesheet: the insets and the safe areas the native bars need (`index.css`).
  if (installed) document.documentElement.setAttribute('data-shell', shellDescriptor()!.idiom);
}

/**
 * Tests only: forget the loaded event check, so a case sees an app shell before it is in. The web's
 * test setup loads it for every file (`src/test/preloadShellEventCheck.ts`).
 */
export function forgetShellEventCheckForTests({
  allowed = true,
}: { allowed?: boolean } = {}): void {
  eventCheck = null;
  loadingEventCheck = null;
  earlyEvents.length = 0;
  eventCheckAllowed = allowed;
}

/** Tests only: forget listeners, requests and the installation. */
export function resetShellBridgeForTests(): void {
  listeners.clear();
  for (const waiting of pending.values()) waiting.reject(new Error('reset'));
  pending.clear();
  installed = false;
}
